import {createServerFn} from '@tanstack/react-start';
import {z} from 'zod';
import {prismaClient} from '@/helpers/prismaClient.server';
import {stripeClient} from '@/helpers/stripeClient.server';
import {
  PAYMENT_READINESS_INCLUDE,
  isReadyToProcessPayments,
} from '@/helpers/stripeAccountReadiness';
import {authMiddleware} from '@/helpers/authMiddleware';
import {requireOrganizerAccess} from '@/helpers/requireAuth.server';

/**
 * Creates a Stripe Connect account for an organizer and stores
 * the account ID. Uses the V2 accounts API with no dashboard access
 * so onboarding is handled entirely via embedded components.
 */
export const createConnectedAccount = createServerFn({method: 'POST'})
  .middleware([authMiddleware])
  .inputValidator((data: unknown): {organizerId: string} => {
    const result = z.object({organizerId: z.string().uuid()}).safeParse(data);
    if (!result.success) {
      throw new Error('Invalid organizer ID');
    }
    return result.data;
  })
  .handler(async ({data, context}) => {
    await requireOrganizerAccess(data.organizerId, context.auth);
    const db = await prismaClient();
    const stripe = await stripeClient();

    const organizer = await db.organizer.findUnique({
      where: {id: data.organizerId},
    });

    if (!organizer) {
      throw new Error('Organizer not found');
    }

    if (organizer.stripeConnectId) {
      return {accountId: organizer.stripeConnectId};
    }

    if (!organizer.contactEmail) {
      throw new Error(
        'Organizer must have a contact email before connecting to Stripe. Please update the organizer profile.',
      );
    }

    const account = await stripe.v2.core.accounts.create({
      display_name: organizer.name,
      contact_email: organizer.contactEmail,
      identity: {
        // Known limitation. We currently only accept businesses from the US.
        country: 'us',
      },
      dashboard: 'none',
      defaults: {
        responsibilities: {
          fees_collector: 'application',
          losses_collector: 'application',
        },
      },
      configuration: {
        customer: {},
        merchant: {
          capabilities: {
            card_payments: {
              requested: true,
            },
          },
        },
        recipient: {
          capabilities: {
            stripe_balance: {
              stripe_transfers: {
                requested: true,
              },
            },
          },
        },
      },
    });

    await db.organizer.update({
      where: {id: data.organizerId},
      data: {stripeConnectId: account.id},
    });

    return {accountId: account.id};
  });

/**
 * Creates an account session for embedded Stripe Connect onboarding.
 * Returns a clientSecret that Connect.js uses to render the onboarding
 * component inline.
 */
export const createAccountSession = createServerFn({method: 'POST'})
  .middleware([authMiddleware])
  .inputValidator((data: unknown): {organizerId: string} => {
    const result = z.object({organizerId: z.string().uuid()}).safeParse(data);
    if (!result.success) {
      throw new Error('Invalid organizer ID');
    }
    return result.data;
  })
  .handler(async ({data, context}) => {
    await requireOrganizerAccess(data.organizerId, context.auth);
    const db = await prismaClient();
    const stripe = await stripeClient();

    const organizer = await db.organizer.findUnique({
      where: {id: data.organizerId},
      select: {stripeConnectId: true},
    });

    if (!organizer?.stripeConnectId) {
      throw new Error('Organizer does not have a Stripe account');
    }

    const accountSession = await stripe.accountSessions.create({
      account: organizer.stripeConnectId,
      components: {
        account_onboarding: {
          enabled: true,
          features: {
            disable_stripe_user_authentication: true,
          },
        },
      },
    });

    return {clientSecret: accountSession.client_secret};
  });

/**
 * Retrieves the Stripe Connect account status for an organizer.
 * Checks onboarding completeness and payment capability, and
 * syncs `canAcceptPayments` in the DB when it differs from Stripe.
 */
export const getConnectAccountStatus = createServerFn({method: 'GET'})
  .middleware([authMiddleware])
  .inputValidator((data: unknown): {organizerId: string} => {
    const result = z.object({organizerId: z.string().uuid()}).safeParse(data);
    if (!result.success) {
      throw new Error('Invalid organizer ID');
    }
    return result.data;
  })
  .handler(async ({data, context}) => {
    await requireOrganizerAccess(data.organizerId, context.auth);
    const db = await prismaClient();
    const stripe = await stripeClient();

    const organizer = await db.organizer.findUnique({
      where: {id: data.organizerId},
      select: {stripeConnectId: true, canAcceptPayments: true},
    });

    if (!organizer?.stripeConnectId) {
      return {
        hasAccount: false,
        readyToProcessPayments: false,
        onboardingComplete: false,
        requirementsStatus: null,
      };
    }

    const account = await stripe.v2.core.accounts.retrieve(
      organizer.stripeConnectId,
      {
        include: [...PAYMENT_READINESS_INCLUDE, 'requirements'],
      }
    );

    const readyToProcessPayments = isReadyToProcessPayments(account);

    const requirementsStatus =
      account.requirements?.summary?.minimum_deadline?.status ?? null;

    const onboardingComplete =
      requirementsStatus !== 'currently_due' &&
      requirementsStatus !== 'past_due';

    // Keep canAcceptPayments in sync with Stripe in both directions
    if (readyToProcessPayments !== organizer.canAcceptPayments) {
      await db.organizer.update({
        where: {id: data.organizerId},
        data: {canAcceptPayments: readyToProcessPayments},
      });
    }

    return {
      hasAccount: true,
      readyToProcessPayments,
      onboardingComplete,
      requirementsStatus,
    };
  });
